import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { PagedResult } from './bank-account.proxy.service';

/** One receipt line (Order.API ReceiptLineDto). A receipt has no PO, so the line carries its own amount. */
export interface PreReceiptItemDto {
  itemCode?: string | null;
  description?: string | null;
  quantity?: number | null;
  /** Item-master unit of measure (shared UnitOfMeasure enum). */
  unitOfMeasure?: number | null;
  /** Tax rate as a decimal fraction (e.g. 0.15 for 15%). Null = no tax. */
  taxRate?: number | null;
  /** Line total. The AP unit price is derived from it (total ÷ quantity) — the API does the same. */
  total?: number | null;
  /** Item classification, already resolved from the item master on the Order side (#4842). Copied straight
   * onto the AP line so a receipt-sourced document is classified without a second item-master round trip. */
  itemClassification?: string | null;
}

/**
 * Kind of provider receipt (Order.API ReceiptType). Maps 1:1 onto the registrable AP document kinds:
 * Fiscal → ApDocumentType.Receipt, Purchase → ApDocumentType.PurchaseReceipt.
 */
export enum ReceiptType {
  /** Provider-issued fiscal document — carries providerInvoiceNumber + fiscalInvoiceNumber. */
  Fiscal = 0,
  /** Self-issued (vendor not SAR-registered). Carries no fiscal number; Finance generates one from the
   *  company CAI when the AP document posts. */
  Purchase = 1,
}

/** Trimmed Order.API ReceiptDto — only what the AP "Pre-Invoice" picker needs (#4767). */
export interface PreReceiptDto {
  id?: string | null;
  /** Order-side receipt number (RCP-{site}-{n}). */
  number?: string | null;
  /** Receipt kind — decides the AP document type (Fiscal → Receipt, Purchase → Purchase Receipt). */
  type?: ReceiptType | null;
  /** Provider's own invoice/document number. Fiscal receipts only. */
  providerInvoiceNumber?: string | null;
  /** Provider's fiscal (CAI) document number. Fiscal receipts only. */
  fiscalInvoiceNumber?: string | null;
  providerReference?: string | null;
  totalAmount?: number | null;
  currency?: number | null;
  /** Order rate to USD — the AP document's exchange rate is taken from the receipt (#4767). */
  exchangeRateToUSD?: number | null;
  /** When the provider issued the receipt — seeds the AP document date, which IS the issue date (#4783). */
  issuedDate?: string | null;
  /** The receipt's own payment term (#4784) — beats the provider default for the AP due date. */
  paymentTermCode?: string | null;
  items?: PreReceiptItemDto[];
}

export interface ReceiptOrderSearchCommand {
  number?: string | null;
  providerInvoiceNumber?: string | null;
  fiscalInvoiceNumber?: string | null;
  providerReference?: string | null;
  /** CustomerOrderStatus filter (Order.API ReceiptSearch.Statuses) — null/empty = any status. */
  statuses?: number[] | null;
}

/** Order.API Receipt search for the AP "register from Pre-Invoice" flow (#4767) — the receipt is the
 * second pre-invoice source alongside the Order Invoice. */
@Injectable({ providedIn: 'root' })
export class ReceiptOrderProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/receipt-order';

  /**
   * Search receipts NOT yet consumed by an AP document (excludeConsumed=true).
   *
   * `matchAny` ORs the populated criteria together, so the picker can match one typed term against several
   * numbers at once. Site scope, the consumed exclusion and the status filter are always AND'd server-side,
   * never folded into that OR — see ReceiptRepository.BuildSearchFilter in Order.API. That distinction is
   * what makes it safe to pass `statuses` alongside matchAny: a Draft receipt cannot slip in by matching
   * one of the number terms.
   */
  public searchUnconsumed(
    command: ReceiptOrderSearchCommand,
    pageNumber = 1,
    pageSize = 10,
    matchAny = false,
  ): Observable<ApiResponse<PagedResult<PreReceiptDto>>> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('excludeConsumed', true)
      .set('matchAny', matchAny);
    return this.httpClient.post<ApiResponse<PagedResult<PreReceiptDto>>>(`${this.baseUrl}/search`, command, { params });
  }
}
